// Estado de red unificado (web + app nativa Capacitor).
//
// `navigator.onLine` es poco confiable dentro del WebView de Capacitor (a menudo
// reporta `true` aunque no haya conexión real). Este módulo mantiene una única
// fuente de verdad en memoria que, en la app nativa, se alimenta de
// @capacitor/network (estado real del dispositivo + eventos), y en la web cae a
// `navigator.onLine` + los eventos `online`/`offline`.
//
// Exporta una lectura SÍNCRONA `isOnline()` para reemplazar `navigator.onLine`
// como drop-in en el drainer y los call-sites existentes, más `onNetworkChange`
// para suscribirse a transiciones (lo usa +layout para disparar el drain al
// reconectar). `initNetworkStatus()` cablea las fuentes y es idempotente.
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";

// Arranque optimista: en SSR/prerender (sin navigator) asumimos online; en
// browser, lo que diga navigator. Se corrige apenas corre initNetworkStatus().
let online = typeof navigator !== "undefined" ? navigator.onLine : true;
let initialized = false;
const listeners = new Set();

function setOnline(value) {
    if (value === online) return;
    online = value;
    for (const cb of listeners) {
        try {
            cb(online);
        } catch {
            /* un listener no debe romper a los demás */
        }
    }
}

// Lectura síncrona del estado actual. Drop-in para `navigator.onLine`.
export function isOnline() {
    return online;
}

// Suscribe un callback a los cambios de red. Devuelve la función de baja.
export function onNetworkChange(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}

// Cablea la fuente de verdad. Idempotente: seguro llamarlo más de una vez.
// En nativo usa @capacitor/network; en web, los eventos del browser.
export async function initNetworkStatus() {
    if (initialized) return;
    initialized = true;

    if (Capacitor.isNativePlatform()) {
        try {
            const status = await Network.getStatus();
            setOnline(status.connected);
            Network.addListener("networkStatusChange", (s) =>
                setOnline(s.connected),
            );
            return;
        } catch {
            // Si el plugin no estuviera disponible, caemos al fallback web.
        }
    }

    if (typeof window !== "undefined") {
        setOnline(navigator.onLine);
        window.addEventListener("online", () => setOnline(true));
        window.addEventListener("offline", () => setOnline(false));
    }
}
